import React, { useState, useEffect, useRef } from "react";
import { Button, Label, TextInput } from "flowbite-react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Datepicker } from "flowbite-react";
import { gamesApi } from "../../api/games";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import dayjs from "dayjs";

export default function AddResultPopup({ isOpen, onClose, gameSettings, refreshResults }) {
  const { tokens } = useAuth();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    gameId: "",
    drawTime: "",
    drawDate: dayjs().format("YYYY-MM-DD"),
    combinations: ""
  });
  const [gameDropdownOpen, setGameDropdownOpen] = useState(false);
  const [timeDropdownOpen, setTimeDropdownOpen] = useState(false);

  // Add refs for dropdown elements
  const gameButtonRef = useRef(null);
  const gameDropdownRef = useRef(null);
  const timeButtonRef = useRef(null);
  const timeDropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setMounted(true), 10);
    } else {
      setMounted(false);
      setTimeout(() => setVisible(false), 300);
      setFormData({
        gameId: "",
        drawTime: "",
        drawDate: dayjs().format("YYYY-MM-DD"),
        combinations: ""
      });
    }
  }, [isOpen]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close game dropdown if clicked outside
      if (gameDropdownOpen && 
          !gameButtonRef.current?.contains(e.target) && 
          !gameDropdownRef.current?.contains(e.target)) {
        setGameDropdownOpen(false);
      }
      
      // Close time dropdown if clicked outside
      if (timeDropdownOpen && 
          !timeButtonRef.current?.contains(e.target) && 
          !timeDropdownRef.current?.contains(e.target)) {
        setTimeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [gameDropdownOpen, timeDropdownOpen]);

  if (!visible) return null;

  const selectedGame = gameSettings.find(g => g.id === formData.gameId);
  const drawTimes = selectedGame?.otherSettings?.drawTime || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await gamesApi.addGameResult(tokens.access.token, {
        gameId: formData.gameId,
        drawTime: formData.drawTime,
        drawDate: formData.drawDate,
        combinations: formData.combinations
      });

      if (response.status === 200) {
        toast.success("Result added successfully");
        await refreshResults();
        onClose();
      } else {
        toast.error("Failed to add result");
      }
    } catch (error) {
      console.error("Error adding result:", error);
      toast.error("Error adding result");
    }
  };

  const formatTimeTo12Hour = (time) => {
    if (!time || typeof time !== 'string' || !time.includes(':')) return "Select draw time";
    const [hour, minute] = time.split(':');
    if (minute === undefined) return "Select draw time";
    const formattedHour = parseInt(hour) % 12 || 12;
    const amPm = parseInt(hour) < 12 ? 'AM' : 'PM';
    return `${formattedHour}:${minute} ${amPm}`;
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center px-4 z-[60]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`w-full max-w-md bg-white rounded-lg p-6 transition-all duration-300 ${
        mounted ? "opacity-100 scale-100" : "opacity-0 scale-95"
      }`}>
        <h2 className="text-lg font-semibold mb-4">Add Game Result</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Game Selection Dropdown */}
          <div className="relative">
            <Label>Select Game</Label>
            <button
              ref={gameButtonRef}
              type="button"
              className="w-full p-2.5 text-sm border rounded-md flex justify-between items-center"
              onClick={() => setGameDropdownOpen(!gameDropdownOpen)}
            >
              <span>{selectedGame?.name || "Select a game"}</span>
              <ChevronDown className="h-4 w-4" />
            </button>
            {gameDropdownOpen && (
              <ul 
                ref={gameDropdownRef}
                className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-md max-h-60 overflow-y-auto"
              >
                {gameSettings.map(game => (
                  <li
                    key={game.id}
                    onClick={() => {
                      setFormData({ ...formData, gameId: game.id, drawTime: "" });
                      setGameDropdownOpen(false);
                    }}
                    className="p-2.5 text-sm hover:bg-gray-100 cursor-pointer"
                  >
                    {game.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Draw Time Dropdown */}
          {formData.gameId && (
            <div className="relative">
              <Label>Draw Time</Label>
              <button
                ref={timeButtonRef}
                type="button"
                className="w-full p-2.5 text-sm border rounded-md flex justify-between items-center"
                onClick={() => setTimeDropdownOpen(!timeDropdownOpen)}
              >
                <span>{formData.drawTime ? formatTimeTo12Hour(formData.drawTime) : "Select draw time"}</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {timeDropdownOpen && (
                <ul
                  ref={timeDropdownRef}
                  className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-md max-h-60 overflow-y-auto"
                >
                  {drawTimes.map(time => (
                    <li
                      key={time}
                      onClick={() => {
                        setFormData({ ...formData, drawTime: time });
                        setTimeDropdownOpen(false);
                      }}
                      className="p-2.5 text-sm hover:bg-gray-100 cursor-pointer"
                    >
                      {formatTimeTo12Hour(time)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Draw Date Calendar */}
          <div>
            <Label>Draw Date</Label>
            <Datepicker
              value={formData.drawDate ? new Date(formData.drawDate) : null}
              onChange={(date) =>
                setFormData({ ...formData, drawDate: dayjs(date).format("YYYY-MM-DD") })
              }
              placeholder="Select date"
              theme={{
                root: {
                  input: {
                    base: "w-full"
                  }
                }
              }}
            />
          </div>

          {/* Combinations Input */}
          <div>
            <Label>Combinations</Label>
            <TextInput
              value={formData.combinations}
              onChange={(e) => setFormData({ ...formData, combinations: e.target.value })}
              placeholder="Enter winning numbers"
              required
            />
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="blue">
              Submit Result
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
} 